import React, { useState, useEffect } from 'react';
import { quoteStepsAPI } from '../../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  GripVertical,
  CheckCircle,
  Globe,
  Code,
  Clock,
  User,
  Calculator,
  Settings,
  DollarSign,
  Download,
  Send,
  ArrowRight,
  ArrowLeft,
  Zap,
  Users,
  Smartphone,
  ShoppingCart,
  Palette,
  Database,
  Shield,
  Star,
  Cloud,
  MessageCircle
} from 'lucide-react';

const QuoteStepsManagement = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStep, setEditingStep] = useState(null);
  const [newStep, setNewStep] = useState({
    step_number: '',
    title: '',
    description: '',
    icon: 'CheckCircle',
    is_active: true,
    // Step 3 specific fields
    step_3_timeline_subtitle: 'Timeline',
    step_3_complexity_subtitle: 'Complexity',
    // Step 5 specific fields
    step_5_subtitle: 'Based on your requirements, here\'s your personalized quote',
    step_5_price_icon: 'DollarSign',
    step_5_time_icon: 'Clock',
    step_5_price_label: 'Estimated Cost',
    step_5_time_label: 'Estimated Timeline',
    step_5_summary_title: 'Project Summary',
    step_5_project_type_label: 'Project Type',
    step_5_features_label: 'Features',
    step_5_timeline_label: 'Timeline',
    step_5_complexity_label: 'Complexity',
    step_5_next_steps_title: 'Next Steps',
    step_5_quote_generated_text: 'Quote generated',
    step_5_schedule_consultation_text: 'Schedule consultation',
    step_5_project_kickoff_text: 'Project kickoff',
    step_5_download_button_text: 'Download Quote',
    step_5_download_button_icon: 'Download',
    step_5_start_project_button_text: 'Start Project',
    step_5_start_project_button_icon: 'Send',
    // Submit Quote button fields
    step_5_submit_button_text: 'Submit Quote',
    step_5_submit_button_icon: 'Send',
    // WhatsApp button fields
    step_5_whatsapp_button_text: 'Order on WhatsApp',
    step_5_whatsapp_button_icon: 'MessageCircle',
    step_5_whatsapp_number: '9515377622'
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Available icons for selection
  const availableIcons = [
    { name: 'CheckCircle', component: CheckCircle },
    { name: 'Globe', component: Globe },
    { name: 'Code', component: Code },
    { name: 'Clock', component: Clock },
    { name: 'User', component: User },
    { name: 'Calculator', component: Calculator },
    { name: 'Settings', component: Settings },
    { name: 'DollarSign', component: DollarSign },
    { name: 'Download', component: Download },
    { name: 'Send', component: Send },
    { name: 'ArrowRight', component: ArrowRight },
    { name: 'ArrowLeft', component: ArrowLeft },
    { name: 'Zap', component: Zap },
    { name: 'Users', component: Users },
    { name: 'Smartphone', component: Smartphone },
    { name: 'ShoppingCart', component: ShoppingCart },
    { name: 'Palette', component: Palette },
    { name: 'Database', component: Database },
    { name: 'Shield', component: Shield },
    { name: 'Star', component: Star },
    { name: 'Cloud', component: Cloud },
    { name: 'MessageCircle', component: MessageCircle }
  ];

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.component : CheckCircle;
  };

  const fetchSteps = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching quote steps...');
      
      // Try admin endpoint first
      let response;
      try {
        response = await quoteStepsAPI.getAdmin();
        console.log('✅ Admin endpoint response:', response);
      } catch (adminError) {
        console.warn('⚠️ Admin endpoint failed, trying public endpoint:', adminError);
        // Fallback to public endpoint
        response = await quoteStepsAPI.getAll();
        console.log('✅ Public endpoint response:', response);
      }
      
             if (response?.data?.success && Array.isArray(response.data.data)) {
         setSteps(response.data.data);
       } else if (response?.data?.success && response.data.data) {
         // Handle case where data might be an object instead of array
         if (Array.isArray(response.data.data)) {
           setSteps(response.data.data);
         } else if (typeof response.data.data === 'object') {
           // If it's an object, try to extract array from it
           const possibleArray = Object.values(response.data.data);
           if (Array.isArray(possibleArray)) {
             setSteps(possibleArray);
           } else {
             setSteps([]);
           }
         } else {
           setSteps([]);
         }
       } else {
         console.warn('Invalid response format or no data:', response);
         setSteps([]);
       }
    } catch (error) {
      console.error('❌ Error fetching steps:', error);
      console.error('Error details:', error.response?.data);
      setMessage({ type: 'error', text: 'Failed to fetch steps' });
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSteps();
  }, []);

  const handleAddStep = async () => {
    try {
      if (!newStep.step_number || !newStep.title) {
        setMessage({ type: 'error', text: 'Step number and title are required' });
        return;
      }

      const response = await quoteStepsAPI.create(newStep);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Step added successfully' });
        setShowAddForm(false);
        setNewStep({
          step_number: '',
          title: '',
          description: '',
          icon: 'CheckCircle',
          is_active: true,
          // Step 3 specific fields
          step_3_timeline_subtitle: 'Timeline',
          step_3_complexity_subtitle: 'Complexity',
          // Step 5 specific fields
          step_5_subtitle: 'Based on your requirements, here\'s your personalized quote',
          step_5_price_icon: 'DollarSign',
          step_5_time_icon: 'Clock',
          step_5_price_label: 'Estimated Cost',
          step_5_time_label: 'Estimated Timeline',
          step_5_summary_title: 'Project Summary',
          step_5_project_type_label: 'Project Type',
          step_5_features_label: 'Features',
          step_5_timeline_label: 'Timeline',
          step_5_complexity_label: 'Complexity',
          step_5_next_steps_title: 'Next Steps',
          step_5_quote_generated_text: 'Quote generated',
          step_5_schedule_consultation_text: 'Schedule consultation',
          step_5_project_kickoff_text: 'Project kickoff',
          step_5_download_button_text: 'Download Quote',
          step_5_download_button_icon: 'Download',
          step_5_start_project_button_text: 'Start Project',
          step_5_start_project_button_icon: 'Send',
          // Submit Quote button fields
          step_5_submit_button_text: 'Submit Quote',
          step_5_submit_button_icon: 'Send',
          // WhatsApp button fields
          step_5_whatsapp_button_text: 'Order on WhatsApp',
          step_5_whatsapp_button_icon: 'MessageCircle',
          step_5_whatsapp_number: '9515377622'
        });
        fetchSteps();
      }
    } catch (error) {
      console.error('Error adding step:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to add step' });
    }
  };

  const handleUpdateStep = async () => {
    try {
      if (!editingStep.step_number || !editingStep.title) {
        setMessage({ type: 'error', text: 'Step number and title are required' });
        return;
      }

      const response = await quoteStepsAPI.update(editingStep.id, editingStep);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Step updated successfully' });
        setEditingStep(null);
        fetchSteps();
      }
    } catch (error) {
      console.error('Error updating step:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update step' });
    }
  };

  const handleDeleteStep = async (id) => {
    if (!window.confirm('Are you sure you want to delete this step?')) {
      return;
    }

    try {
      const response = await quoteStepsAPI.delete(id);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Step deleted successfully' });
        fetchSteps();
      }
    } catch (error) {
      console.error('Error deleting step:', error);
      setMessage({ type: 'error', text: 'Failed to delete step' });
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    const newSteps = [...steps];
    const [movedStep] = newSteps.splice(fromIndex, 1);
    newSteps.splice(toIndex, 0, movedStep);

    // Update step numbers
    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      step_number: index + 1
    }));

    try {
      const response = await quoteStepsAPI.reorder(
        reorderedSteps.map(step => ({ id: step.id, step_number: step.step_number }))
      );
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Steps reordered successfully' });
        fetchSteps();
      }
    } catch (error) {
      console.error('Error reordering steps:', error);
      setMessage({ type: 'error', text: 'Failed to reorder steps' });
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  // Render step-specific fields based on step number
  const renderStepSpecificFields = (stepData, isEditing = false) => {
    const stepNumber = isEditing ? stepData.step_number : newStep.step_number;
    
    if (stepNumber === 3) {
      return (
        <div className="md:col-span-2 border-t pt-4 mt-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Step 3 Specific Fields</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timeline Subtitle
              </label>
              <input
                type="text"
                value={isEditing ? stepData.step_3_timeline_subtitle || 'Timeline' : newStep.step_3_timeline_subtitle}
                onChange={(e) => {
                  if (isEditing) {
                    setEditingStep({ ...stepData, step_3_timeline_subtitle: e.target.value });
                  } else {
                    setNewStep({ ...newStep, step_3_timeline_subtitle: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Timeline"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Complexity Subtitle
              </label>
              <input
                type="text"
                value={isEditing ? stepData.step_3_complexity_subtitle || 'Complexity' : newStep.step_3_complexity_subtitle}
                onChange={(e) => {
                  if (isEditing) {
                    setEditingStep({ ...stepData, step_3_complexity_subtitle: e.target.value });
                  } else {
                    setNewStep({ ...newStep, step_3_complexity_subtitle: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Complexity"
              />
            </div>
          </div>
        </div>
      );
    }
    
    if (stepNumber === 5) {
      return (
        <div className="md:col-span-2 border-t pt-4 mt-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Step 5 Specific Fields</h4>
          <div className="space-y-4">
            {/* Step 5 Subtitle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Step 5 Subtitle
              </label>
              <textarea
                value={isEditing ? stepData.step_5_subtitle || 'Based on your requirements, here\'s your personalized quote' : newStep.step_5_subtitle}
                onChange={(e) => {
                  if (isEditing) {
                    setEditingStep({ ...stepData, step_5_subtitle: e.target.value });
                  } else {
                    setNewStep({ ...newStep, step_5_subtitle: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                rows="2"
                placeholder="Based on your requirements, here's your personalized quote"
              />
            </div>

            {/* Price and Time Icons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price Icon
                </label>
                <select
                  value={isEditing ? stepData.step_5_price_icon || 'DollarSign' : newStep.step_5_price_icon}
                  onChange={(e) => {
                    if (isEditing) {
                      setEditingStep({ ...stepData, step_5_price_icon: e.target.value });
                    } else {
                      setNewStep({ ...newStep, step_5_price_icon: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Icon
                </label>
                <select
                  value={isEditing ? stepData.step_5_time_icon || 'Clock' : newStep.step_5_time_icon}
                  onChange={(e) => {
                    if (isEditing) {
                      setEditingStep({ ...stepData, step_5_time_icon: e.target.value });
                    } else {
                      setNewStep({ ...newStep, step_5_time_icon: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Price and Time Labels */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price Label
                </label>
                <input
                  type="text"
                  value={isEditing ? stepData.step_5_price_label || 'Estimated Cost' : newStep.step_5_price_label}
                  onChange={(e) => {
                    if (isEditing) {
                      setEditingStep({ ...stepData, step_5_price_label: e.target.value });
                    } else {
                      setNewStep({ ...newStep, step_5_price_label: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Estimated Cost"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Label
                </label>
                <input
                  type="text"
                  value={isEditing ? stepData.step_5_time_label || 'Estimated Timeline' : newStep.step_5_time_label}
                  onChange={(e) => {
                    if (isEditing) {
                      setEditingStep({ ...stepData, step_5_time_label: e.target.value });
                    } else {
                      setNewStep({ ...newStep, step_5_time_label: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Estimated Timeline"
                />
              </div>
            </div>

            {/* Summary Section */}
            <div className="border-t pt-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Project Summary Section</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Summary Title
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_summary_title || 'Project Summary' : newStep.step_5_summary_title}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_summary_title: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_summary_title: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Project Summary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project Type Label
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_project_type_label || 'Project Type' : newStep.step_5_project_type_label}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_project_type_label: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_project_type_label: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Project Type"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Features Label
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_features_label || 'Features' : newStep.step_5_features_label}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_features_label: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_features_label: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Features"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Timeline Label
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_timeline_label || 'Timeline' : newStep.step_5_timeline_label}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_timeline_label: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_timeline_label: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Timeline"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Complexity Label
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_complexity_label || 'Complexity' : newStep.step_5_complexity_label}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_complexity_label: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_complexity_label: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Complexity"
                  />
                </div>
              </div>
            </div>

            {/* Next Steps Section */}
            <div className="border-t pt-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Next Steps Section</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Next Steps Title
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_next_steps_title || 'Next Steps' : newStep.step_5_next_steps_title}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_next_steps_title: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_next_steps_title: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Next Steps"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quote Generated Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_quote_generated_text || 'Quote generated' : newStep.step_5_quote_generated_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_quote_generated_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_quote_generated_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Quote generated"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Schedule Consultation Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_schedule_consultation_text || 'Schedule consultation' : newStep.step_5_schedule_consultation_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_schedule_consultation_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_schedule_consultation_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Schedule consultation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project Kickoff Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_project_kickoff_text || 'Project kickoff' : newStep.step_5_project_kickoff_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_project_kickoff_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_project_kickoff_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Project kickoff"
                  />
                </div>
              </div>
            </div>

            {/* Buttons Section */}
            <div className="border-t pt-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Buttons Section</h5>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Configure the buttons that appear in Step 5 of the quote process. Changes will be reflected on the website immediately.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Download Button Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_download_button_text || 'Download Quote' : newStep.step_5_download_button_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_download_button_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_download_button_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Download Quote"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Download Button Icon
                  </label>
                  <select
                    value={isEditing ? stepData.step_5_download_button_icon || 'Download' : newStep.step_5_download_button_icon}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_download_button_icon: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_download_button_icon: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {availableIcons.map(icon => (
                      <option key={icon.name} value={icon.name}>
                        {icon.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Project Button Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_start_project_button_text || 'Start Project' : newStep.step_5_start_project_button_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_start_project_button_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_start_project_button_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Start Project"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Project Button Icon
                  </label>
                  <select
                    value={isEditing ? stepData.step_5_start_project_button_icon || 'Send' : newStep.step_5_start_project_button_icon}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_start_project_button_icon: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_start_project_button_icon: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {availableIcons.map(icon => (
                      <option key={icon.name} value={icon.name}>
                        {icon.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Submit Quote Button Fields */}
                <div className="md:col-span-2">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Submit Quote Button:</strong> This button saves the quote to the database and makes it available in the Admin Panel &gt; Quotes section.
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Submit Quote Button Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_submit_button_text || 'Submit Quote' : newStep.step_5_submit_button_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_submit_button_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_submit_button_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Submit Quote"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Submit Quote Button Icon
                  </label>
                  <select
                    value={isEditing ? stepData.step_5_submit_button_icon || 'Send' : newStep.step_5_submit_button_icon}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_submit_button_icon: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_submit_button_icon: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {availableIcons.map(icon => (
                      <option key={icon.name} value={icon.name}>
                        {icon.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* WhatsApp Button Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WhatsApp Button Text
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_whatsapp_button_text || 'Order on WhatsApp' : newStep.step_5_whatsapp_button_text}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_whatsapp_button_text: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_whatsapp_button_text: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Order on WhatsApp"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WhatsApp Button Icon
                  </label>
                  <select
                    value={isEditing ? stepData.step_5_whatsapp_button_icon || 'MessageCircle' : newStep.step_5_whatsapp_button_icon}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_whatsapp_button_icon: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_whatsapp_button_icon: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {availableIcons.map(icon => (
                      <option key={icon.name} value={icon.name}>
                        {icon.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WhatsApp Number
                  </label>
                  <input
                    type="text"
                    value={isEditing ? stepData.step_5_whatsapp_number || '9515377622' : newStep.step_5_whatsapp_number}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingStep({ ...stepData, step_5_whatsapp_number: e.target.value });
                      } else {
                        setNewStep({ ...newStep, step_5_whatsapp_number: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="9515377622"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              Quote Steps Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage the steps displayed on the quote page
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Step
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{message.text}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add Step Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add New Step</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Step Number
              </label>
              <input
                type="number"
                value={newStep.step_number}
                onChange={(e) => setNewStep({ ...newStep, step_number: parseInt(e.target.value) || '' })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter step number"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title
              </label>
              <input
                type="text"
                value={newStep.title}
                onChange={(e) => setNewStep({ ...newStep, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter step title"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={newStep.description}
                onChange={(e) => setNewStep({ ...newStep, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                rows="3"
                placeholder="Enter step description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon
              </label>
              <select
                value={newStep.icon}
                onChange={(e) => setNewStep({ ...newStep, icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                {availableIcons.map(icon => (
                  <option key={icon.name} value={icon.name}>
                    {icon.name}
                  </option>
                ))}
              </select>
            </div>
            
                         <div className="flex items-center">
               <label className="flex items-center">
                 <input
                   type="checkbox"
                   checked={newStep.is_active}
                   onChange={(e) => setNewStep({ ...newStep, is_active: e.target.checked })}
                   className="mr-2"
                 />
                 <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
               </label>
             </div>
           </div>
           
           {/* Step-specific fields */}
           {renderStepSpecificFields(newStep, false)}
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddStep}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add Step
            </button>
          </div>
        </div>
      )}

      {/* Steps List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quote Steps ({steps.length})
          </h3>
          
          {!Array.isArray(steps) || steps.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              {!Array.isArray(steps) ? 'Error loading steps' : 'No steps found. Add your first step to get started.'}
            </p>
          ) : (
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${
                    !step.is_active ? 'opacity-50 bg-gray-50 dark:bg-gray-900' : ''
                  }`}
                >
                  {editingStep?.id === step.id ? (
                    // Edit Form
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Step Number
                          </label>
                          <input
                            type="number"
                            value={editingStep.step_number}
                            onChange={(e) => setEditingStep({ ...editingStep, step_number: parseInt(e.target.value) || '' })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Title
                          </label>
                          <input
                            type="text"
                            value={editingStep.title}
                            onChange={(e) => setEditingStep({ ...editingStep, title: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Description
                          </label>
                          <textarea
                            value={editingStep.description}
                            onChange={(e) => setEditingStep({ ...editingStep, description: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            rows="3"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Icon
                          </label>
                          <select
                            value={editingStep.icon}
                            onChange={(e) => setEditingStep({ ...editingStep, icon: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          >
                            {availableIcons.map(icon => (
                              <option key={icon.name} value={icon.name}>
                                {icon.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                                                 <div className="flex items-center">
                           <label className="flex items-center">
                             <input
                               type="checkbox"
                               checked={editingStep.is_active}
                               onChange={(e) => setEditingStep({ ...editingStep, is_active: e.target.checked })}
                               className="mr-2"
                             />
                             <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                           </label>
                         </div>
                       </div>
                       
                       {/* Step-specific fields */}
                       {renderStepSpecificFields(editingStep, true)}
                      
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setEditingStep(null)}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateStep}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-semibold">
                            {step.step_number}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {(() => {
                            const IconComponent = getIconComponent(step.icon);
                            return <IconComponent className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
                          })()}
                          
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {step.title}
                            </h4>
                            {step.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {step.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingStep(step)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit step"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStep(step.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                          title="Delete step"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteStepsManagement; 